import { useQuery } from '@apollo/client'
import { Slide, Slider } from 'pure-react-carousel'
import { ReactElement, useMemo } from 'react'

import { LoadingSpinner } from '@syconium/little-miss-figgy'
import { ViewportSizes, useIsViewport } from '@syconium/little-miss-figgy'

import { gql } from '../../../../__generated__/graphql/catalog'
import { isValidNostoPlacementId } from '../../../../app/_components/chrome/scripts/NostoScript'
import { useFetchNostoPlacementData } from '../../../../app/_hooks/useFetchNostoPlacementData'
import { useNextQuery } from '../../../../app/_hooks/useNextQuery'
import { useLocalization } from '../../../../app/_providers/LocalizationProvider.client'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'

import { CarouselControls } from './CarouselControls'
import { Body, Carousel, Dots, LoadingSpinnerWrapper, Title } from './styles'
import { UpsellTile } from './UpsellTile'

export const FETCH_UPSELL_CONFIGURATION = gql(`
  query FetchUpsellConfiguration($regionId: String!) {
    siteMessages(regionId: $regionId) {
      miniCartMessages {
        __typename
        id
        upsellSkus
        nostoUpsellId
      }
    }
  }
`)

export const FETCH_UPSELL_PRODUCTS = gql(`
  query GetVariantsForCartUpsell($skusForVariantGroups: [String!]) {
    productCatalogVariants(skus: $skusForVariantGroups) {
      ...UpsellTileVariant
    }
  }
`)

export const Upsells = (): ReactElement | null => {
  const {
    cart: { beforeYouGo: beforeYouGoText },
    magnolia: {
      general: { loading: loadingText },
    },
  } = useTranslation()
  const { region } = useLocalization()

  const isMediumOrLargeViewport = useIsViewport([ViewportSizes.medium, ViewportSizes.large])
  const naturalSlideHeight = isMediumOrLargeViewport ? 150 : 160

  const {
    data: upsellConfigurationData,
    status: upsellConfigurationStatus,
    error: upsellConfigurationError,
  } = useNextQuery(FETCH_UPSELL_CONFIGURATION, {
    variables: {
      regionId: region.id,
    },
  })

  const { nostoUpsellId: requestedNostoPlacementId, upsellSkus: defaultUpsellSkus } =
    upsellConfigurationData?.siteMessages?.miniCartMessages ?? {}

  const nostoEnabled = !!requestedNostoPlacementId

  const defaultNostoPlacementId = 'cartpage-nosto-4'
  const nostoPlacementId = isValidNostoPlacementId(requestedNostoPlacementId)
    ? requestedNostoPlacementId
    : defaultNostoPlacementId

  const {
    data: { skusForVariantGroups: nostoUpsellSkus },
    status: nostoStatus,
  } = useFetchNostoPlacementData(nostoPlacementId)

  const skusForVariantGroups = useMemo(() => {
    return (nostoEnabled ? nostoUpsellSkus : []).concat(defaultUpsellSkus ?? []).slice(0, 3)
  }, [defaultUpsellSkus, nostoEnabled, nostoUpsellSkus])

  const {
    data,
    error: productsError,
    loading: productsLoading,
  } = useQuery(FETCH_UPSELL_PRODUCTS, {
    skip: skusForVariantGroups === undefined || skusForVariantGroups.length === 0,
    variables: { skusForVariantGroups },
  })

  const error = useMemo(() => {
    return !!upsellConfigurationError || !!productsError || nostoStatus === 'rejected'
  }, [nostoStatus, productsError, upsellConfigurationError])

  const loading = useMemo(() => {
    return (
      upsellConfigurationStatus === 'pending' ||
      productsLoading ||
      (nostoEnabled && nostoStatus === 'pending')
    )
  }, [nostoEnabled, nostoStatus, productsLoading, upsellConfigurationStatus])

  const variants = data?.productCatalogVariants ?? []

  if (!loading && !error && variants.length === 0) return null

  // TODO: Handle errors
  // Why do we never handle errors? -_-

  return (
    <Body>
      <Title>{beforeYouGoText}</Title>

      {loading ? (
        <LoadingSpinnerWrapper>
          <LoadingSpinner label={loadingText} size='24px' />
        </LoadingSpinnerWrapper>
      ) : (
        <Carousel
          naturalSlideHeight={naturalSlideHeight}
          dragEnabled={!isMediumOrLargeViewport}
          naturalSlideWidth={280}
          totalSlides={variants.length}
        >
          <Slider
            aria-label={`You are currently on a slider for adding additional products to your cart that has ${variants.length} tiles. The next element is the product name, then product size, then add to card button.`}
            style={{ overflowX: 'clip', overflowY: 'visible' }}
            tabIndex={-1}
          >
            {variants.map((variant, i: number) => {
              return (
                <Slide index={i} key={variant.id} tabIndex={-1}>
                  <UpsellTile
                    variant={variant}
                    i={i}
                    nostoPlacementId={nostoEnabled ? nostoPlacementId : undefined}
                  />
                </Slide>
              )
            })}
          </Slider>
          {isMediumOrLargeViewport && <CarouselControls />}
          <Dots />
        </Carousel>
      )}
    </Body>
  )
}
